import type { DistributionBoardBOQQuantityEntry } from "components/SmartSurveyor/ReviewPage/DistributionBoardBOQByQuantity/types";
import { sortByPrefixGroupedDesc } from "./sortByPrefixGroupedDesc";
import { compareOccurrencesByPage } from "./compareOccurrencesByPage";
import type {
  BoardDiagramWithoutPagesV3,
  BoardItemTypeV3,
  BoardItemOccurrenceV3,
  DiagramMeta,
  LvMatch,
} from "./types";

/**
 * Normalizes the backend `diagram` field into `DiagramMeta`. The backend
 * returns `{ id: number, diagramKey: string, name: string }` — `id` is the
 * DB primary key, `diagramKey` is the semantic identifier from ingestion.
 */
function normalizeDiagramField(raw: unknown): DiagramMeta {
  if (
    typeof raw === "object" &&
    raw !== null &&
    "id" in raw &&
    "diagramKey" in raw &&
    "name" in raw
  ) {
    const obj = raw as { id: number; diagramKey: string; name: string };
    return {
      id: Number(obj.id),
      diagramKey: String(obj.diagramKey),
      name: String(obj.name),
    };
  }
  return { id: 0, diagramKey: "", name: "" };
}

/**
 * Groups distribution board data by diagram, then by item type,
 * collecting per-page occurrences under each item type.
 *
 * Input hierarchy:  Entry (item) -> Diagram -> Page
 * Output hierarchy: Diagram -> ItemType -> PageOccurrences
 */
export function transformDistributionBoardV3(
  entries: DistributionBoardBOQQuantityEntry[] | undefined,
): BoardDiagramWithoutPagesV3[] {
  if (!entries?.length) return [];

  const diagramMap = new Map<
    string,
    {
      meta: DiagramMeta;
      sourceFile?: string;
      items: Map<
        string,
        {
          diagram_item_type_id?: number;
          item: string;
          text?: string;
          unit: string;
          lvMatch?: LvMatch;
          occurrences: BoardItemOccurrenceV3[];
        }
      >;
    }
  >();

  for (const entry of entries) {
    const compositeKey = `${entry.item}\0${entry.text ?? ""}`;
    for (const diagram of entry.diagrams) {
      const meta = normalizeDiagramField(diagram.diagram);
      const diagramKey = entry.sourceFile
        ? `${entry.sourceFile}\0${meta.diagramKey}`
        : meta.diagramKey;
      let bucket = diagramMap.get(diagramKey);
      if (!bucket) {
        bucket = { meta, sourceFile: entry.sourceFile, items: new Map() };
        diagramMap.set(diagramKey, bucket);
      }

      let itemData = bucket.items.get(compositeKey);
      if (!itemData) {
        itemData = {
          diagram_item_type_id: diagram.diagram_item_type_id!,
          item: entry.item,
          text: entry.text,
          unit: entry.unit,
          lvMatch: entry.lvMatch ?? undefined,
          occurrences: [],
        };
        bucket.items.set(compositeKey, itemData);
      }

      for (const page of diagram.pages) {
        itemData.occurrences.push({
          occurrence_id: page.occurrence_id,
          page: page.page,
          imageUrl: page.imageUrl ?? "",
          pdfUrl: page.pdfUrl ?? "",
          widthPx: page.widthPx ?? 0,
          heightPx: page.heightPx ?? 0,
          imageScaleFactor: page.imageScaleFactor ?? 1,
          count: page.count,
          circuits: page.circuits ?? [],
        });
      }
    }
  }

  const result: BoardDiagramWithoutPagesV3[] = [];

  for (const [, bucket] of diagramMap) {
    const itemTypes: BoardItemTypeV3[] = [];

    for (const [, itemData] of bucket.items) {
      itemData.occurrences.sort(compareOccurrencesByPage);

      const totalCount = itemData.occurrences.reduce(
        (sum, occ) => sum + occ.count,
        0,
      );

      itemTypes.push({
        diagram_item_type_id: itemData.diagram_item_type_id!,
        item: itemData.item,
        text: itemData.text,
        unit: itemData.unit,
        lvMatch: itemData.lvMatch,
        totalCount,
        occurrences: itemData.occurrences,
      });
    }

    const sortedItemTypes = sortByPrefixGroupedDesc({
      items: itemTypes,
      getLabel: (itemType) => itemType.item,
      getSize: (itemType) => itemType.totalCount,
    });

    const totalCount = itemTypes.reduce((sum, t) => sum + t.totalCount, 0);
    result.push({
      diagram: bucket.meta,
      itemTypes: sortedItemTypes,
      totalCount,
      sourceFile: bucket.sourceFile ?? "",
    });
  }

  result.sort((a, b) => a.diagram.name.localeCompare(b.diagram.name));

  return result;
}
